import { apiClient } from "./client";
import type {
  BulkActionParams,
  BulkActionResponse,
  CallSessionCounts,
  Company,
  CompanyBulkArchiveResponse,
  CompanyBulkDeleteResponse,
  CompanyBulkUnarchiveResponse,
  CompanyCreate,
  CompanyDetail,
  CompanyListParams,
  CompanyListResponse,
  CompanyStats,
  CompanyUpdate,
  ImportResponse,
  ImportValidateResponse,
} from "./types";

interface ListCompaniesParams extends CompanyListParams {
  metadata_eq?: string[];
}

function buildCompanyParams(params: ListCompaniesParams): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    cleaned[key] = value;
  }
  return cleaned;
}

export async function listCompanies(
  slug: string,
  params: ListCompaniesParams = {},
): Promise<CompanyListResponse> {
  const res = await apiClient.get<CompanyListResponse>(
    `/projects/${slug}/companies`,
    {
      params: buildCompanyParams(params),
      paramsSerializer: { indexes: null },
    },
  );
  return res.data;
}

export async function exportCompaniesCSV(
  slug: string,
  params: ListCompaniesParams = {},
): Promise<void> {
  const response = await apiClient.get<Blob>(
    `/projects/${slug}/companies/export`,
    {
      params: buildCompanyParams(params),
      paramsSerializer: { indexes: null },
      responseType: "blob",
    },
  );

  const disposition = response.headers["content-disposition"] as
    | string
    | undefined;
  const fallback = `prism_export_${slug}.csv`;
  const filename = parseFilename(disposition) ?? fallback;

  const blob = new Blob([response.data], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function parseFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;
  // Prefer RFC 5987 `filename*=UTF-8''…` over the legacy `filename="…"`.
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ""));
    } catch {
      // fall through to legacy
    }
  }
  const legacy = /filename=("([^"]+)"|([^;]+))/i.exec(contentDisposition);
  if (legacy) return (legacy[2] ?? legacy[3] ?? "").trim() || null;
  return null;
}

/** Call-Center landing page counters — one HTTP roundtrip,
 *  three numbers. Backend computes them with COUNT(*) FILTER per
 *  bucket, so they always match what /companies?session_type=… will
 *  return. Excludes archived rows server-side — and, when `userId` is
 *  passed, leads currently locked to *other* operators, so the
 *  "available" badges reflect what this user could actually start a
 *  session on. */
export async function getCallSessionCounts(
  slug: string,
  userId?: string,
): Promise<CallSessionCounts> {
  const res = await apiClient.get<CallSessionCounts>(
    `/projects/${slug}/companies/call-session-counts`,
    { params: { user_id: userId || undefined } },
  );
  return res.data;
}

export async function getCompany(
  slug: string,
  id: number,
): Promise<CompanyDetail> {
  const res = await apiClient.get<CompanyDetail>(
    `/projects/${slug}/companies/${id}`,
  );
  return res.data;
}

export async function updateCompany(
  slug: string,
  id: number,
  payload: CompanyUpdate,
): Promise<Company> {
  const res = await apiClient.patch<Company>(
    `/projects/${slug}/companies/${id}`,
    payload,
  );
  return res.data;
}

export async function createCompany(
  slug: string,
  payload: CompanyCreate,
): Promise<Company> {
  const res = await apiClient.post<Company>(
    `/projects/${slug}/companies`,
    payload,
  );
  return res.data;
}

export async function rescoreCompany(
  slug: string,
  id: number,
): Promise<Company> {
  const res = await apiClient.post<Company>(
    `/projects/${slug}/companies/${id}/rescore`,
  );
  return res.data;
}

export interface RescoreAllResponse {
  status: "started";
  total_companies: number;
}

export async function rescoreAllCompanies(
  slug: string,
): Promise<RescoreAllResponse> {
  const res = await apiClient.post<RescoreAllResponse>(
    `/projects/${slug}/companies/rescore-all`,
  );
  return res.data;
}

// ─── Archive / unarchive / delete ─────────────────────────────────────────

export async function archiveCompany(
  slug: string,
  id: number,
): Promise<Company> {
  const res = await apiClient.post<Company>(
    `/projects/${slug}/companies/${id}/archive`,
  );
  return res.data;
}

export async function unarchiveCompany(
  slug: string,
  id: number,
): Promise<Company> {
  const res = await apiClient.post<Company>(
    `/projects/${slug}/companies/${id}/unarchive`,
  );
  return res.data;
}

export async function deleteCompany(
  slug: string,
  id: number,
): Promise<void> {
  await apiClient.delete(`/projects/${slug}/companies/${id}`);
}

export async function bulkArchiveCompanies(
  slug: string,
  ids: number[],
): Promise<CompanyBulkArchiveResponse> {
  const res = await apiClient.post<CompanyBulkArchiveResponse>(
    `/projects/${slug}/companies/bulk-archive`,
    { company_ids: ids },
  );
  return res.data;
}

export async function bulkUnarchiveCompanies(
  slug: string,
  ids: number[],
): Promise<CompanyBulkUnarchiveResponse> {
  const res = await apiClient.post<CompanyBulkUnarchiveResponse>(
    `/projects/${slug}/companies/bulk-unarchive`,
    { company_ids: ids },
  );
  return res.data;
}

export async function bulkDeleteCompanies(
  slug: string,
  ids: number[],
): Promise<CompanyBulkDeleteResponse> {
  const res = await apiClient.post<CompanyBulkDeleteResponse>(
    `/projects/${slug}/companies/bulk-delete`,
    { company_ids: ids },
  );
  return res.data;
}

/** Centralized helper for every /bulk-action call. Wraps the wire
 *  schema — `{action, payload, mode, company_ids | filters}` — so call
 *  sites only pass intent (the action + its payload) and selection
 *  (mode + companyIds | filters). No other module should POST to
 *  /bulk-action directly; route everything through this helper to keep
 *  the schema in one place. */
export async function bulkAction(
  params: BulkActionParams,
): Promise<BulkActionResponse> {
  const body: Record<string, unknown> = {
    action: params.action,
    payload: params.payload,
    mode: params.mode,
    ...(params.mode === "selected"
      ? { company_ids: params.companyIds ?? [] }
      : { filters: params.filters ?? {} }),
  };
  const res = await apiClient.post<BulkActionResponse>(
    `/projects/${params.slug}/companies/bulk-action`,
    body,
  );
  return res.data;
}

export async function getStats(slug: string): Promise<CompanyStats> {
  const res = await apiClient.get<CompanyStats>(
    `/projects/${slug}/companies/stats`,
  );
  return res.data;
}

export async function validateImport(
  slug: string,
  file: File,
): Promise<ImportValidateResponse> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<ImportValidateResponse>(
    `/projects/${slug}/companies/import/validate`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function importCompanies(
  slug: string,
  file: File,
): Promise<ImportResponse> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post<ImportResponse>(
    `/projects/${slug}/companies/import`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

/** Distinct sector list for a project, served by the backend. */
export async function getSectors(slug: string): Promise<{ sectors: string[] }> {
  const res = await apiClient.get<{ sectors: string[] }>(
    `/projects/${slug}/sectors`,
  );
  return res.data;
}

/** Autocomplete catalog for the advanced-filter builder. The backend
 *  resolves `field` against first-class columns + JSONB metadata and
 *  returns up to ~50 distinct values, optionally filtered by `search`.
 *  Falls back to an empty list on 404 / error so the picker degrades
 *  to free-text entry instead of breaking the UI. */
export async function getFilterValues(
  slug: string,
  field: string,
  search = "",
): Promise<string[]> {
  try {
    const res = await apiClient.get<{ values: string[] }>(
      `/projects/${slug}/companies/filter-values/${encodeURIComponent(field)}`,
      { params: search ? { search } : undefined },
    );
    return res.data.values ?? [];
  } catch {
    return [];
  }
}

// Helper to encode metadata filters as the backend expects: ["key:value", ...]
export function encodeMetadataEq(
  filters: Record<string, string | boolean | null | undefined>,
): string[] {
  const out: string[] = [];
  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined || value === "") continue;
    out.push(`${key}:${value}`);
  }
  return out;
}
